import * as React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import CustomersTable from 'src/features/customers/components/tables/CustomersTable';
import CustomerDashboard from 'src/features/customers/components/CustomerDashboard';
import HomeLayout from 'src/features/home/components/HomeLayout';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import TaxReasonsTable from 'src/features/taxReasons/components/tables/TaxReasonsTable';
import ServicePlanCategoriesTable from 'src/features/servicePlanCategory/components/tables/ServicePlanCategoriesTable';
import ServicePlansTable from 'src/features/servicePlans/components/tables/ServicePlansTable';
import ServicePlanDashboard from 'src/features/servicePlans/components/ServicePlanDashboard';
import ServicePlanItemDashboard from 'src/features/servicePlanItems/components/ServicePlanItemDashboard';
import BillingSimulationPage from 'src/features/billingSimulation/components/BillingSimulationPage';
import BillingEntitiesTable from 'src/features/billing/components/tables/BillingEntitiesTable';
import BillingEntityDashboard from 'src/features/billing/components/BillingEntityDashboard';
import AccountingHeadersTable from 'src/features/accountingHeaders/components/tables/AccountingHeadersTable';
import AccountingRangesTable from 'src/features/accountingRanges/components/tables/AccountingRangesTable';
import AccountingRangeDashboard from 'src/features/accountingRanges/components/AccountingRangeDashboard';
import SubscriptionDashboard from 'src/features/subscriptions/components/SubscriptionDashboard';
import SubscriptionItemDashboard from 'src/features/subscriptionItems/components/SubscriptionItemDashboard';
import PaymentDashboard from 'src/features/payments/components/PaymentDashboard';
import FinancialDocumentDashboard from 'src/features/financialDocuments/components/FinancialDocumentDashboard';
import UnitsTable from 'src/features/units/components/tables/UnitsTable';
import PaymentsLayout from 'src/features/payments/components/PaymentsLayout';
import SubscriptionLayout from 'src/features/subscriptions/components/SubscriptionLayout';
import FinancialDocumentsLayout from 'src/features/financialDocuments/components/FinancialDocumentsLayout';
import PaymentMethodsLayout from 'src/features/paymentMethods/components/PaymentMethodsLayout';
import ChargesLayout from 'src/features/charges/components/ChargesLayout';
import DiscountPoliciesLayout from 'src/features/discountPolicies/components/DiscountPoliciesLayout';
import DiscountPolicyDashboard from 'src/features/discountPolicies/components/DiscountPolicyDashboard';
import VoucherTypesLayout from 'src/features/voucherTypes/components/VoucherTypesLayout';
import VoucherTypeDashboard from 'src/features/voucherTypes/components/VoucherTypeDashboard';
import VouchersLayout from 'src/features/vouchers/components/VouchersLayout';
import VoucherDashboard from 'src/features/vouchers/components/VoucherDashboard';
import DiscountLayout from 'src/features/discounts/components/DiscountLayout';
import DiscountDashboard from 'src/features/discounts/components/DiscountDashboard';
import BillingEntityBankAccountsLayout from 'src/features/billingEntityBankAccounts/components/BillingEntityBankAccountsLayout';
import MoneyAccountsLayout from 'src/features/moneyAccounts/components/MoneyAccountsLayout';
import WebhookEndpointsLayout from 'src/features/webhook-endpoints/components/WebhookEndpointsLayout';
import WebhookEventsLayout from 'src/features/webhook-events/components/WebhookEventsLayout';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { useIntl } from 'react-intl';
import { generalMessages } from 'src/utils/messages';
import RolesTable from 'src/features/roles/components/tables/RolesTable';
import RoleDashboard from 'src/features/roles/components/RoleDashboard';
import UsersLayout from 'src/features/users/components/UsersLayout';
import ProfileRoutes from 'src/features/profile/routes';

function HomeRoutes() {
    const { setForceLeftStaticPanel } = useSidePanel();
    const { formatMessage } = useIntl();

    React.useEffect(() => {
        setForceLeftStaticPanel(true);
        return () => setForceLeftStaticPanel(false);
    }, [setForceLeftStaticPanel]);

    const permissionAccessCustomers = useHasPermissions([PERMISSION.ACCESS_CUSTOMERS]);
    const permissionPayments = useHasPermissions([PERMISSION.ACCESS_PAYMENTS]);
    const permissionSubscriptions = useHasPermissions([PERMISSION.ACCESS_SUBSCRIPTIONS]);
    const permissionTaxReasons = useHasPermissions([PERMISSION.ACCESS_TAX_REASONS]);
    const permissionServicePlans = useHasPermissions([PERMISSION.ACCESS_SERVICE_PLANS]);
    const permissionDiscounts = useHasPermissions([PERMISSION.ACCESS_DISCOUNTS]);
    const permissionVouchers = useHasPermissions([PERMISSION.ACCESS_VOUCHERS]);
    const permissionCharges = useHasPermissions([PERMISSION.ACCESS_CHARGES]);
    const permissionFinancialDocuments = useHasPermissions([PERMISSION.ACCESS_FINANCIAL_DOCUMENTS]);
    const permissionBillingEntities = useHasPermissions([PERMISSION.ACCESS_BILLING_ENTITIES]);
    const permissionAccountingHeadings = useHasPermissions([PERMISSION.ACCESS_ACCOUNTING_HEADINGS]);
    const permissionAccountingRanges = useHasPermissions([PERMISSION.ACCESS_ACCOUNTING_RANGES]);
    const permissionUnits = useHasPermissions([PERMISSION.ACCESS_UNITS]);
    const permissionMoneyAccounts = useHasPermissions([PERMISSION.ACCESS_MONEY_ACCOUNTS]);
    const permissionAccessRoles = useHasPermissions([PERMISSION.ACCESS_ROLES]);
    const permissionAccessUsers = useHasPermissions([PERMISSION.ACCESS_USERS]);

    const defaultRoutePath = (() => {
        if (permissionAccessCustomers) return '/customers';
        if (permissionAccessCustomers) return '/payment-methods';
        if (permissionAccessUsers) return '/users';
        if (permissionAccessRoles) return '/roles';
        if (permissionSubscriptions) return '/subscriptions';
        if (permissionTaxReasons) return '/tax-reasons';
        if (permissionServicePlans) return '/service-plan-categories';
        if (permissionDiscounts) return '/discount-policies';
        if (permissionVouchers) return '/vouchers';
        if (permissionCharges) return '/charges';
        if (permissionPayments) return '/payments';
        if (permissionFinancialDocuments) return '/financial-documents';
        if (permissionBillingEntities) return '/billing-entity';
        if (permissionMoneyAccounts) return '/money-accounts';
        if (permissionAccountingHeadings) return '/accounting-headers';
        if (permissionAccountingRanges) return '/accounting-ranges';
        if (permissionUnits) return '/units';

        return '/no-access';
    })();

    return (
        <Routes>
            <Route element={<HomeLayout />}>
                <Route index element={<Navigate to={defaultRoutePath} replace />} />
                {permissionAccessCustomers && (
                    <>
                        <Route path="customers" element={<CustomersTable />} />
                        <Route path="customers/:customerId" element={<CustomerDashboard />} />
                        <Route path="payment-methods" element={<PaymentMethodsLayout />} />
                    </>
                )}
                {permissionAccessUsers && <Route path="users" element={<UsersLayout />} />}
                {permissionAccessRoles && (
                    <>
                        <Route path="roles" element={<RolesTable />} />
                        <Route path="roles/:roleId" element={<RoleDashboard />} />
                    </>
                )}
                {permissionSubscriptions && (
                    <>
                        <Route path="subscriptions" element={<SubscriptionLayout />} />
                        <Route path="subscriptions/:subscriptionId" element={<SubscriptionDashboard />} />
                        <Route
                            path="subscriptions/:subscriptionId/subscription-item/:subscriptionItemId"
                            element={<SubscriptionItemDashboard />}
                        />
                    </>
                )}

                {permissionCharges && <Route path="charges" element={<ChargesLayout />} />}
                {permissionPayments && (
                    <>
                        <Route path="payments" element={<PaymentsLayout />} />
                        <Route path="payments/:paymentId" element={<PaymentDashboard />} />
                    </>
                )}

                {permissionFinancialDocuments && (
                    <>
                        <Route path="financial-documents" element={<FinancialDocumentsLayout />} />
                        <Route
                            path="financial-documents/:financialDocumentId"
                            element={<FinancialDocumentDashboard />}
                        />
                    </>
                )}
                {permissionTaxReasons && <Route path="tax-reasons" element={<TaxReasonsTable />} />}

                {permissionServicePlans && (
                    <>
                        <Route path="service-plan-categories" element={<ServicePlanCategoriesTable />} />
                        <Route path="service-plans" element={<ServicePlansTable />} />
                        <Route path="service-plans/:servicePlanId" element={<ServicePlanDashboard />} />
                        <Route
                            path="service-plans/:servicePlanId/simulate-billing"
                            element={<BillingSimulationPage />}
                        />
                        <Route path="service-plan-item/:servicePlanItemId" element={<ServicePlanItemDashboard />} />
                    </>
                )}

                {permissionBillingEntities && (
                    <>
                        <Route path="billing-entity" element={<BillingEntitiesTable />} />
                        <Route path="billing-entity/:entityId" element={<BillingEntityDashboard />} />
                        <Route path="bank-accounts" element={<BillingEntityBankAccountsLayout />} />
                    </>
                )}

                {permissionMoneyAccounts && <Route path="money-accounts" element={<MoneyAccountsLayout />} />}

                {permissionAccountingHeadings && (
                    <Route path="accounting-headers" element={<AccountingHeadersTable />} />
                )}

                {permissionAccountingRanges && (
                    <>
                        <Route path="accounting-ranges" element={<AccountingRangesTable />} />
                        <Route path="accounting-ranges/:rangeId" element={<AccountingRangeDashboard />} />
                    </>
                )}

                {permissionUnits && <Route path="units" element={<UnitsTable />} />}

                {permissionDiscounts && (
                    <>
                        <Route path="discount-policies" element={<DiscountPoliciesLayout />} />
                        <Route path="discount-policies/:discountPolicyId" element={<DiscountPolicyDashboard />} />
                        <Route path="discounts" element={<DiscountLayout />} />
                        <Route path="discounts/:discountId" element={<DiscountDashboard />} />
                    </>
                )}

                {permissionVouchers && (
                    <>
                        <Route path="voucher-types" element={<VoucherTypesLayout />} />
                        <Route path="voucher-types/:voucherTypeId" element={<VoucherTypeDashboard />} />
                        <Route path="vouchers" element={<VouchersLayout />} />
                        <Route path="vouchers/:voucherId" element={<VoucherDashboard />} />
                    </>
                )}

                <Route path="webhook-endpoints" element={<WebhookEndpointsLayout />} />
                <Route path="webhook-events" element={<WebhookEventsLayout />} />

                <Route path="profile/*" element={<ProfileRoutes />} />

                <Route
                    path="/no-access"
                    element={
                        <div className="flex h-full items-center justify-center">
                            {formatMessage(generalMessages.noAccess)}
                        </div>
                    }
                />
            </Route>
        </Routes>
    );
}

export default HomeRoutes;
